import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import React, { useMemo } from 'react';
import { SmallTitleWithSubSpan } from 'src/components/common/common';
import Grid_Excel from 'src/components/common/gridWrapper/Grid_Excel';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';
import { defaultHRM1040_R01HeaderDto, HRM1040_R01HeaderDto, HRM1040_R01HeaderGridRow } from './Dto_HRM1040_R01';
import { getHRM1040_R01DeductFamilyCount } from './Util_HRM1040_R01';
import { useHRM1040_R01Store } from './Store_HRM1040_R01';

/** 사원 목록 참조와 행 전환 함수를 받는다. */
interface Props {
    gridRef: React.RefObject<AgGridReact<HRM1040_R01HeaderGridRow> | null>; // 사원 그리드
    onSelect: (row: HRM1040_R01HeaderDto) => void; // 사원 선택
}

/**
 * 조회 전용 사원 목록과 선택 사원의 공제가족 수를 표시한다.
 * @param gridRef 사원 그리드 참조
 * @param onSelect 사원 행 클릭 시 선택 사원을 바꾸는 함수. 변경이 있으면 확인창을 먼저 띄운다.
 * @returns 사원 목록 그리드 JSX
 */
export const Table_HRM1040_R01 = ({ gridRef, onSelect }: Props) => {
    // 화면 Store에서 사원 목록, 선택 사원, 공제가족 계산용 가족 목록·삭제 표시, 컬럼·스크롤 상태를 가져온다.
    const { HRM1040_R01List, selectedHRM1040_R01, HRM1040_R01FamilyList, deletedHRM1040_R01FamilyList,
        columnState, setColumnState, scrollTop, setScrollTop } = useHRM1040_R01Store();
    // 재직 컬럼 표시명에 쓸 재직상태 공통코드 목록을 가져온다.
    const { empstsCdList } = useCommonCodeStore();
    // 그리드 기본 설정: 행 ID 생성, 행·셀 CSS 규칙 생성 함수, 빈 행을 채운 표시 목록을 받는다.
    const { getRowId, createRowClassRules, createCellClassRules, paddedGridData } = useGridSettingContent<HRM1040_R01HeaderGridRow>({
        gridRef, key: 'valueIndex', gridData: HRM1040_R01List,
        defaultGridData: defaultHRM1040_R01HeaderDto, selectedRow: selectedHRM1040_R01,
    });
    // 선택 사원의 공제가족 수. 편집 중인 가족 목록으로 바로 다시 계산한다.
    const selectedDeductFamilyCount = useMemo(
        () => getHRM1040_R01DeductFamilyCount(HRM1040_R01FamilyList, deletedHRM1040_R01FamilyList),
        [HRM1040_R01FamilyList, deletedHRM1040_R01FamilyList],
    );
    // 그리드에 넘길 행 목록. 선택 사원 행의 공제가족 수만 편집 중인 값으로 바꾼다.
    // 컬럼 정의 대신 행 데이터에 반영해 가족을 편집해도 컬럼 정의가 다시 만들어지지 않게 한다.
    const displayGridData = useMemo(() => paddedGridData.map(row =>
        // 선택 사원 행이면 공제가족 수를 덮어쓴 복사본, 아니면 원래 행이다.
        selectedHRM1040_R01 && row.empNo === selectedHRM1040_R01.empNo
            ? { ...row, deductFamilyCount: selectedDeductFamilyCount } : row,
    ), [paddedGridData, selectedHRM1040_R01, selectedDeductFamilyCount]);
    /** 사원 목록 컬럼 정의이다. 모든 컬럼은 조회 전용이다. */
    const baseColumnDefs = useMemo<ColDef<HRM1040_R01HeaderGridRow>[]>(() => [
        // 공통 행 번호 컬럼 설정을 풀어 넣고 제목과 너비만 바꾼다.
        ...getDefaultRowIndexColumnConfig().map(column => ({ ...column, headerName: 'No', width: 56 })),
        { headerName: '사원번호', colId: 'empNo', field: 'empNo', width: 100 },
        { headerName: '사원명', colId: 'empNm', field: 'empNm', width: 88 },
        { headerName: '부서', colId: 'orgNm', field: 'orgNm', width: 96 },
        { headerName: '직위', colId: 'positionNm', field: 'positionNm', width: 60 },
        {
            headerName: '공제가족', colId: 'deductFamilyCount', field: 'deductFamilyCount', width: 72,
            valueFormatter: params => params.value == null ? '' : `${params.value}명`
        },
        {
            headerName: '재직', colId: 'empSts', field: 'empSts', width: 62,
            // ?.·??: 재직상태 공통코드에서 이름을 찾고, 없으면 코드 값을, 값도 없으면 빈칸을 표시한다.
            valueFormatter: params => empstsCdList.find(item => item.minorCd === String(params.value))?.codeNm
                || String(params.value ?? '')
        },
    ], [empstsCdList]);
    // 기본 컬럼 순서. 의존성: 컬럼 정의가 바뀔 때만 다시 만든다.
    const defaultColumnState = useMemo(() => buildDefaultColumnStateFromDefs(baseColumnDefs, []), [baseColumnDefs]);
    /** 저장된 컬럼 순서·너비를 공통 컬럼 정의 함수로 복원한다. */
    const columnDefs = useMemo(() => columnState === null ? baseColumnDefs : GridCustomColumnDefs<HRM1040_R01HeaderGridRow>({
        baseColumnDefs, columnState, headerNames: {
            rowIndex: 'No', empNo: '사원번호', empNm: '사원명', orgNm: '부서',
            positionNm: '직위', deductFamilyCount: '공제가족', empSts: '재직'
        },
    }), [baseColumnDefs, columnState]);
    /** 조회한 사원 수만 그리드 하단의 사원번호 칸에 표시한다. */
    const pinnedBottomRow = useMemo(() => [{
        empNo: `${HRM1040_R01List.length.toLocaleString()}건`,
    }], [HRM1040_R01List.length]);
    /** 공통 셀 규칙으로 하단 고정 행의 건수를 가운데 정렬한다. 편집·포인터 컬럼이 없는 조회 전용 설정이다. */
    const cellCssDefaultColDef = {
        ...defaultColDef,
        editable: false,
        cellClassRules: createCellClassRules<HRM1040_R01HeaderGridRow>({
            idKey: 'valueIndex',
            selectedList: selectedHRM1040_R01 ? [selectedHRM1040_R01.valueIndex] : [],
            deletedList: [],
            disabledInsertedColumns: [],
            disabledColumns: [],
            pointerInsertedColumns: [],
            pointerColumns: [],
        }),
    };

    return (
        <section className="flex flex-col h-full min-h-0">
            {/* 우클릭 메뉴와 컬럼 상태 저장을 담당하는 공통 감싸기 컴포넌트 */}
            <GridContextWrapper<HRM1040_R01HeaderGridRow>
                gridRef={gridRef}
                paddedGridData={displayGridData}
                defaultRowData={defaultHRM1040_R01HeaderDto}
                baseColumnDefs={columnDefs}
                defaultColumnState={defaultColumnState}
                setColumnState={setColumnState}
                localStorageKey="HRM1040_R01-grid-column-state">
                {/* 공통 파란 막대 제목으로 사원 목록 영역을 표시한다. */}
                <SmallTitleWithSubSpan title="사원 목록" />
                {/* 사원 목록 그리드: 편집 없이 선택 행 표시, 행 클릭 선택, 하단 건수 행을 연결한다. */}
                <Grid_Excel<HRM1040_R01HeaderGridRow>
                    ref={gridRef}
                    rowData={displayGridData}
                    getRowId={getRowId}
                    columnDefs={columnDefs}
                    defaultColDef={cellCssDefaultColDef}
                    rowClassRules={createRowClassRules<HRM1040_R01HeaderGridRow>({
                        idKey: 'valueIndex',
                        selectedList: selectedHRM1040_R01 ? [selectedHRM1040_R01.valueIndex] : [],
                        deletedList: [],
                    })}
                    onRowClicked={event => {
                        // 하단 고정 행과 사원번호가 없는 빈 행은 선택하지 않는다.
                        if (!event.node.rowPinned && event.data?.empNo) onSelect(event.data);
                    }}
                    pinnedBottomRowData={pinnedBottomRow}
                    columnStateKey="HRM1040_R01-grid-column-state"
                    setColumnState={setColumnState}
                    getDefaultColumnState={() => defaultColumnState}
                    scrollTop={scrollTop}
                    setScrollTop={setScrollTop} />
            </GridContextWrapper>
        </section>
    );
};
